import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import {
  useGetSeoWithChatGptMutation,
  useGenerateSeoWithChatGptMutation,
  useTranslateSeoV3Mutation
} from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/src-refactor/modules/store/store";
import { useFormContext } from "react-hook-form";
import SeoGenerateAdapter from "../../../../adapters/form/seo-generate-request";

import { MxChipInputHelpAdapterValue } from "@rf-l1/form/text-fields/MxInputChip";
import { IPropertyForm } from "../../util/schemas/property-schema/propertySchema";
import {
  CPropertyForm,
  CPropertyFormKeys
} from "../../util/schemas/property-schema/propertySchema";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";
import { GenerateSeoWithChatGptBody } from "@/src-refactor/modules/store/qrtk/services/realestate/values/generate-seo-with-chatgpt.values";

interface SeoFormState {
  isLoadingGenerateAll: boolean;
  isLoadingGenerateTitle: boolean;
  isLoadingGenerateDescription: boolean;
  isLoadingGenerateDescriptionShort: boolean;
  isLoadingGenerateKeywords: boolean;
  isLoadingTranslateSection: boolean;
  isLoadingGenerateAiDescription: boolean;
  [key: string]: boolean; // Add index signature
  // Define the state structure here
}

const initialState: SeoFormState = {
  isLoadingGenerateAll: false,
  isLoadingGenerateTitle: false,
  isLoadingGenerateDescription: false,
  isLoadingGenerateDescriptionShort: false,
  isLoadingGenerateKeywords: false,
  isLoadingTranslateSection: false,
  isLoadingGenerateAiDescription: false
  // Initialize the state here
};

const showLoadingToast = (promise: Promise<any>, message: string) => {
  return toast.promise(
    promise,
    {
      loading: message,
      success: `${message} completado`,
      error: `${message} fallido`
    },
    {
      position: "bottom-left"
    }
  );
};

export const generateAll = createAsyncThunk(
  "seoForm/generateAll",
  async ({ promise }: { promise: any }, { dispatch }) => {
    return showLoadingToast(promise, "Generando información del SEO...");
  }
);

export const generateTitle = createAsyncThunk(
  "seoForm/generateTitle",
  async ({ promise }: { promise: any }, { dispatch }) => {
    return showLoadingToast(promise, "Generando título SEO...");
  }
);

export const generateDescription = createAsyncThunk(
  "seoForm/generateDescription",
  async ({ promise }: { promise: any }, { dispatch }) => {
    return showLoadingToast(promise, "Generando descripción SEO...");
  }
);

export const generateDescriptionShort = createAsyncThunk(
  "seoForm/generateDescriptionShort",
  async ({ promise }: { promise: any }, { dispatch }) => {
    return showLoadingToast(promise, "Generando descripción corta SEO...");
  }
);

export const generateKeywords = createAsyncThunk(
  "seoForm/generateKeywords",
  async ({ promise }: { promise: any }, { dispatch }) => {
    return showLoadingToast(promise, "Generando palabras clave SEO...");
  }
);

export const translateSection = createAsyncThunk(
  "seoForm/translateSection",
  async ({ promise }: { promise: any }, { dispatch }) => {
    return showLoadingToast(promise, "Traduciendo sección SEO...");
  }
);

export const generateAiDescription = createAsyncThunk(
  "seoForm/generateAiDescription",
  async ({ promise }: { promise: any }, { dispatch }) => {
    return showLoadingToast(promise, "Generando descripción IA...");
  }
);

export const SeoFormSlice = createSlice({
  name: "seoForm",
  initialState,
  reducers: {
    generateSeo: (
      state,
      action: PayloadAction<{ field: string; value: string }>
    ) => {
      // @ts-ignore
      state[field] = value;
    },
    resetSeoFormState: () => initialState // Add reset reducer
  },
  extraReducers: builder => {
    builder
      /**
       * @deprecated
       */
      .addCase(generateAll.pending, state => {
        state.isLoadingGenerateAll = true;
      })
      /**
       * @deprecated
       */
      .addCase(generateAll.fulfilled, state => {
        state.isLoadingGenerateAll = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateAll.rejected, state => {
        state.isLoadingGenerateAll = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateTitle.pending, state => {
        state.isLoadingGenerateTitle = true;
      })
      /**
       * @deprecated
       */
      .addCase(generateTitle.fulfilled, state => {
        state.isLoadingGenerateTitle = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateTitle.rejected, state => {
        state.isLoadingGenerateTitle = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateDescription.pending, state => {
        state.isLoadingGenerateDescription = true;
      })
      /**
       * @deprecated
       */
      .addCase(generateDescription.fulfilled, state => {
        state.isLoadingGenerateDescription = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateDescription.rejected, state => {
        state.isLoadingGenerateDescription = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateDescriptionShort.pending, state => {
        state.isLoadingGenerateDescriptionShort = true;
      })
      /**
       * @deprecated
       */
      .addCase(generateDescriptionShort.fulfilled, state => {
        state.isLoadingGenerateDescriptionShort = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateDescriptionShort.rejected, state => {
        state.isLoadingGenerateDescriptionShort = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateKeywords.pending, state => {
        state.isLoadingGenerateKeywords = true;
      })
      /**
       * @deprecated
       */
      .addCase(generateKeywords.fulfilled, state => {
        state.isLoadingGenerateKeywords = false;
      })
      /**
       * @deprecated
       */
      .addCase(generateKeywords.rejected, state => {
        state.isLoadingGenerateKeywords = false;
      })
      /**
       * @deprecated
       */
      .addCase(translateSection.pending, state => {
        state.isLoadingTranslateSection = true;
      })
      /**
       * @deprecated
       */
      .addCase(translateSection.fulfilled, state => {
        state.isLoadingTranslateSection = false;
      })
      /**
       * @deprecated
       */
      .addCase(translateSection.rejected, state => {
        state.isLoadingTranslateSection = false;
      })

      //
      .addCase(generateAiDescription.pending, state => {
        state.isLoadingGenerateAiDescription = true;
      })
      .addCase(generateAiDescription.fulfilled, state => {
        state.isLoadingGenerateAiDescription = false;
      })
      .addCase(generateAiDescription.rejected, state => {
        state.isLoadingGenerateAiDescription = false;
      });
  }
});

export const { generateSeo, resetSeoFormState } = SeoFormSlice.actions; // Export the reset action

export const useSeoFormActions = () => {
  const dispatch = useAppDispatch();
  const states = useSelector((state: { seoForm: SeoFormState }) => state.seoForm);
  const { getValues, setValue } = useFormContext();
  const [GET_SEO_WITH_CHAT_GPT_M] = useGetSeoWithChatGptMutation();
  const [TRANSLATE_V3_M] = useTranslateSeoV3Mutation();
  const [GENERATE_SEO_WITH_CHAT_GPT_M] = useGenerateSeoWithChatGptMutation();

  const pickValid = (s?: string | null) => {
    if (typeof s !== "string") return undefined;
    const t = s.trim();
    if (!t || t.toLowerCase() === "skip") return undefined;
    return t;
  };
  /**
   * @deprecated
   */
  const generateAllAction = async () => {
    const ctx = getValues();
    const query = SeoGenerateAdapter(ctx as IPropertyForm);

    const promise = GET_SEO_WITH_CHAT_GPT_M({ query })
      .unwrap()
      .then(res => {
        setValue(
          CPropertyFormKeys.seo_title_es_property,
          res.info?.es?.title || CPropertyForm.seo_title_es_property,
          {
            shouldValidate: true
          }
        );
        setValue(
          CPropertyFormKeys.seo_title_en_property,
          res.info?.en?.title || CPropertyForm.seo_title_en_property,
          {
            shouldValidate: true
          }
        );
        setValue(
          CPropertyFormKeys.seo_description_es_property,
          res.info?.es?.description || CPropertyForm.seo_description_es_property,
          {
            shouldValidate: true
          }
        );
        setValue(
          CPropertyFormKeys.seo_description_en_property,
          res.info?.en?.description || CPropertyForm.seo_description_en_property,
          {
            shouldValidate: true
          }
        );
        // setValue(
        //   CPropertyFormKeys.seo_short_description_es_property,
        //   res.info.es.short_description ||
        //     CPropertyForm.seo_short_description_es_property,
        //   {
        //     shouldValidate: true
        //   }
        // );
        // setValue(
        //   CPropertyFormKeys.seo_short_description_en_property,
        //   res.info.en.short_description ||
        //     CPropertyForm.seo_short_description_en_property,
        //   {
        //     shouldValidate: true
        //   }
        // );
        // setValue(
        //   CPropertyFormKeys.seo_keywords_es_property,
        //   res.info.es.keywords?.split(":") || CPropertyForm.seo_keywords_en_property,
        //   {
        //     shouldValidate: true
        //   }
        // );
        // setValue(
        //   CPropertyFormKeys.seo_keywords_en_property,
        //   res.info.en.keywords?.split(":") || CPropertyForm.seo_keywords_en_property,
        //   {
        //     shouldValidate: true
        //   }
        // );
      })
      .catch(e => HToastCatchServerError(e));

    dispatch(generateAll({ promise }));
  };

  const generateSeoWithChatGptAction = async (body: GenerateSeoWithChatGptBody) => {
    dispatch(
      generateAiDescription({
        promise: GENERATE_SEO_WITH_CHAT_GPT_M({ body })
          .unwrap()
          .then(res => {
            const infoEs = res?.object?.info?.es;
            const infoEn = res?.object?.info?.en;
            const descriptionHTLM = res?.html;

            if (infoEs?.title) {
              setValue(CPropertyFormKeys.seo_title_es_property, infoEs.title, {
                shouldValidate: true
              });
            }
            if (infoEn?.title) {
              setValue(CPropertyFormKeys.seo_title_en_property, infoEn.title, {
                shouldValidate: true
              });
            }
            if (descriptionHTLM?.es) {
              setValue(
                CPropertyFormKeys.seo_description_es_property,
                descriptionHTLM?.es,
                { shouldValidate: true }
              );
            }
            if (descriptionHTLM?.en) {
              setValue(
                CPropertyFormKeys.seo_description_en_property,
                descriptionHTLM?.en,
                { shouldValidate: true }
              );
            }
            // Opcional: keywords y short_description si decides usarlos
          })
          .catch(e => HToastCatchServerError(e))
      })
    );
  };
  /**
   * @deprecated
   */
  const generateTitleAction = async () => {
    const ctx = getValues();
    const query = SeoGenerateAdapter(ctx as IPropertyForm);

    dispatch(
      generateTitle({
        promise: GET_SEO_WITH_CHAT_GPT_M({ query })
          .unwrap()
          .then(res => {
            setValue(
              CPropertyFormKeys.seo_title_es_property,
              res.info?.es?.title || CPropertyForm.seo_title_es_property,
              {
                shouldValidate: true
              }
            );
            setValue(
              CPropertyFormKeys.seo_title_en_property,
              res.info?.en?.title || CPropertyForm.seo_title_en_property,
              {
                shouldValidate: true
              }
            );
          })
          .catch(e => HToastCatchServerError(e))
      })
    );
  };
  /**
   * @deprecated
   */
  const generateDescriptionAction = async () => {
    const ctx = getValues();
    const query = SeoGenerateAdapter(ctx as IPropertyForm);

    dispatch(
      generateDescription({
        promise: GET_SEO_WITH_CHAT_GPT_M({ query })
          .unwrap()
          .then(res => {
            setValue(
              CPropertyFormKeys.seo_description_es_property,
              res.info?.es?.description || CPropertyForm.seo_description_es_property,
              {
                shouldValidate: true
              }
            );
            setValue(
              CPropertyFormKeys.seo_description_en_property,
              res.info?.en?.description || CPropertyForm.seo_description_en_property,
              {
                shouldValidate: true
              }
            );
          })
          .catch(e => HToastCatchServerError(e))
      })
    );
  };
  /**
   * @deprecated
   */
  const generateDescriptionShortAction = async () => {
    const ctx = getValues();
    const query = SeoGenerateAdapter(ctx as IPropertyForm);

    dispatch(
      generateDescriptionShort({
        promise: GET_SEO_WITH_CHAT_GPT_M({ query })
          .unwrap()
          .then(res => {
            // setValue(
            //   CPropertyFormKeys.seo_short_description_es_property,
            //   res.info?.es?.short_description ||
            //     CPropertyForm.seo_short_description_es_property,
            //   {
            //     shouldValidate: true
            //   }
            // );
            // setValue(
            //   CPropertyFormKeys.seo_short_description_en_property,
            //   res.info.en.short_description ||
            //     CPropertyForm.seo_short_description_en_property,
            //   {
            //     shouldValidate: true
            //   }
            // );
          })
          .catch(e => HToastCatchServerError(e))
      })
    );
  };
  /**
   * @deprecated
   */
  const generateKeywordsAction = async () => {
    const ctx = getValues();
    const query = SeoGenerateAdapter(ctx as IPropertyForm);

    dispatch(
      generateKeywords({
        promise: GET_SEO_WITH_CHAT_GPT_M({ query })
          .unwrap()
          .then(res => {
            // setValue(
            //   CPropertyFormKeys.seo_keywords_es_property,
            //   MxChipInputHelpAdapterValue(res.info?.es?.keywords) ||
            //     CPropertyForm.seo_keywords_es_property,
            //   {
            //     shouldValidate: true
            //   }
            // );
            // setValue(
            //   CPropertyFormKeys.seo_keywords_en_property,
            //   MxChipInputHelpAdapterValue(res.info?.en?.keywords) ||
            //     CPropertyForm.seo_keywords_en_property,
            //   {
            //     shouldValidate: true
            //   }
            // );
          })
          .catch(e => HToastCatchServerError(e))
      })
    );
  };
  /**
   * @deprecated
   */
  const translateSectionAction = async (
    direction: "es_to_en" | "en_to_es" = "es_to_en"
  ) => {
    const form = getValues() as IPropertyForm;

    const isEsToEn = direction === "es_to_en";
    const title = isEsToEn
      ? form.seo_title_es_property
      : form.seo_title_en_property;
    const description = isEsToEn
      ? form.seo_description_es_property
      : form.seo_description_en_property;

    const body = {
      // Both fields are required by API; use 'skip' when not available
      title: title && title.trim().length > 0 ? title : "skip",
      description:
        description && description.trim().length > 0 ? description : "skip",
      source_lang: isEsToEn ? ("es" as const) : ("en" as const),
      target_lang: isEsToEn ? ("en" as const) : ("es" as const)
    };

    dispatch(
      translateSection({
        promise: TRANSLATE_V3_M({ body })
          .unwrap()
          .then(res => {
            const target = isEsToEn ? res.en : res.es;
            const tTitle = pickValid(target?.title);
            const tDesc = pickValid(target?.description);
            if (isEsToEn) {
              if (tTitle) {
                setValue(
                  CPropertyFormKeys.seo_title_en_property,
                  tTitle,
                  { shouldValidate: true }
                );
              }
              if (tDesc) {
                setValue(
                  CPropertyFormKeys.seo_description_en_property,
                  tDesc,
                  { shouldValidate: true }
                );
              }
            } else {
              if (tTitle) {
                setValue(
                  CPropertyFormKeys.seo_title_es_property,
                  tTitle,
                  { shouldValidate: true }
                );
              }
              if (tDesc) {
                setValue(
                  CPropertyFormKeys.seo_description_es_property,
                  tDesc,
                  { shouldValidate: true }
                );
              }
            }
          })
          .catch(e => HToastCatchServerError(e))
      })
    );
  };

  // Translate a single field (title or description) from the edited language to the other
  const translateFieldAction = async (
    field: "title" | "description",
    direction: "es_to_en" | "en_to_es" = "es_to_en"
  ) => {
    const form = getValues() as IPropertyForm;
    const isEsToEn = direction === "es_to_en";

    // Pick only the source piece we want to translate
    const srcTitle = isEsToEn ? form.seo_title_es_property : form.seo_title_en_property;
    const srcDescription = isEsToEn
      ? form.seo_description_es_property
      : form.seo_description_en_property;

    const body = {
      // API requires both; send 'skip' for the one we don't want or when empty
      title:
        field === "title"
          ? srcTitle && srcTitle.trim().length > 0
            ? srcTitle
            : "skip"
          : "skip",
      description:
        field === "description"
          ? srcDescription && srcDescription.trim().length > 0
            ? srcDescription
            : "skip"
          : "skip",
      source_lang: isEsToEn ? ("es" as const) : ("en" as const),
      target_lang: isEsToEn ? ("en" as const) : ("es" as const)
    };

    dispatch(
      translateSection({
        promise: TRANSLATE_V3_M({ body })
          .unwrap()
          .then(res => {
            const target = isEsToEn ? res.en : res.es;
            if (!target) return;
            if (field === "title") {
              const t = pickValid(target.title);
              if (t) {
                setValue(
                  isEsToEn
                    ? CPropertyFormKeys.seo_title_en_property
                    : CPropertyFormKeys.seo_title_es_property,
                  t,
                  { shouldValidate: true }
                );
              }
            }
            if (field === "description") {
              const d = pickValid(target.description);
              if (d) {
                setValue(
                  isEsToEn
                    ? CPropertyFormKeys.seo_description_en_property
                    : CPropertyFormKeys.seo_description_es_property,
                  d,
                  { shouldValidate: true }
                );
              }
            }
          })
          .catch(e => HToastCatchServerError(e))
      })
    );
  };
  /**
   * @deprecated
   */
  const generateSeoAction = (field: string, value: string) =>
    dispatch(generateSeo({ field, value }));

  return {
    generateAll: generateAllAction,
    generateTitle: generateTitleAction,
    generateDescription: generateDescriptionAction,
    generateDescriptionShort: generateDescriptionShortAction,
    generateKeywords: generateKeywordsAction,
    translateSection: translateSectionAction,
    translateField: translateFieldAction,
    generateSeoWithChatGpt: generateSeoWithChatGptAction,
    generateSeo: generateSeoAction,
    ...states
  };
};

export const useSeoFormSelectors = (state: { seoForm: SeoFormState }) => ({
  isLoadingGenerateAll: state.seoForm.isLoadingGenerateAll,
  isLoadingGenerateTitle: state.seoForm.isLoadingGenerateTitle,
  isLoadingGenerateDescription: state.seoForm.isLoadingGenerateDescription,
  isLoadingGenerateDescriptionShort: state.seoForm.isLoadingGenerateDescriptionShort,
  isLoadingGenerateKeywords: state.seoForm.isLoadingGenerateKeywords,
  isLoadingTranslateSection: state.seoForm.isLoadingTranslateSection,
  isLoadingGenerateAiDescription: state.seoForm.isLoadingGenerateAiDescription
});

export default SeoFormSlice.reducer;

function useAppDispatch(): AppDispatch {
  return useDispatch<AppDispatch>();
}

export const useResetSeoFormState = () => {
  const dispatch = useAppDispatch();
  return () => dispatch(resetSeoFormState());
};
